import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TabGroup from "@/components/ui/tab";
import Chip from "@/components/ui/chip";
import { ArrowRightLong, RightChevron } from "@/components/icons";
import type {
  DashboardAnnouncement,
  DashboardAnnouncementTagType,
} from "../../types/dashboard.types";
import { clientAnnouncementsApi } from "@/features/client-announcements/services/client-announcements.api";
import type { ClientAnnouncementListItem } from "@/features/client-announcements/types/client-announcements.types";
import { ANNOUNCEMENT_DISPLAY_TYPE } from "@/features/client-announcements/types/client-announcements.types";
import { renderPaginationNumbers } from "@/utils/pagination";
import {
  ArrowLeft as ArrowLeftIcon,
  ArrowRight as ArrowRightIcon,
} from "@/components/icons";
import styles from "./DashboardAnnouncementsList.module.scss";

const TAG_CHIP_TYPE: Record<
  DashboardAnnouncementTagType,
  "success" | "primary" | "warning" | "default"
> = {
  system: "primary",
  reminder: "warning",
  catalog: "success",
  education: "success",
};

const PAGE_SIZE = 5;

function formatTime(iso?: string): string {
  // Duyuruların saat bilgisi gösterilmez; yalnızca DD.MM.YY tarih formatı kullanılır.
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Tag metninden chip tipini çıkarır; bilinmeyenler "system" olarak düşer. */
function inferTagType(tag: string): DashboardAnnouncementTagType {
  const t = tag.toLowerCase();
  if (
    t.includes("eğitim") ||
    t.includes("egitim") ||
    t.includes("kurs") ||
    t.includes("education")
  ) {
    return "education";
  }
  if (t.includes("katalog") || t.includes("catalog")) return "catalog";
  if (
    t.includes("hatırlat") ||
    t.includes("hatirlat") ||
    t.includes("reminder")
  ) {
    return "reminder";
  }
  return "system";
}

function mapToListDisplay(
  item: ClientAnnouncementListItem,
): DashboardAnnouncement {
  const tag = item.tags?.[0] || "Duyuru";
  return {
    id: item.id,
    title: item.title,
    imageUrl: undefined, // optional: resolve from item.imageFileID if file URL endpoint exists
    time: formatTime(item.publishedDate),
    tag,
    tagType: inferTagType(tag),
  };
}

interface DashboardAnnouncementsListProps {
  viewAllPath?: string;
}

export const DashboardAnnouncementsList: React.FC<DashboardAnnouncementsListProps> = ({
  viewAllPath = "/dashboard/client-announcements",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [items, setItems] = useState<DashboardAnnouncement[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const tab = activeTab === 0 ? "general" : "private";

  useEffect(() => {
    clientAnnouncementsApi
      // Popup duyurular girişte modal olarak gösteriliyor; burada tekrar
      // listelenmesin diye yalnızca anasayfa duyuruları çekiliyor.
      .getAnnouncements({
        type: tab,
        displayType: ANNOUNCEMENT_DISPLAY_TYPE.HOMEPAGE,
        page,
        limit: PAGE_SIZE,
      })
      .then((res) => {
        setItems((res.items || []).map(mapToListDisplay));
        setTotal(res.total ?? 0);
      })
      .catch((err) => {
        if (import.meta.env.DEV) {
          console.warn(
            "[DashboardAnnouncementsList] getAnnouncements failed",
            err,
          );
        }
        setItems([]);
        setTotal(0);
      });
  }, [page, tab]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>
          {t("dashboard.announcements.title")}
        </h3>
        <button
          type="button"
          className={styles.sectionLink}
          onClick={() =>
            navigate(viewAllPath, {
              state: { activeTab },
            })
          }
          aria-label={t("dashboard.announcements.viewAll")}
        >
          <RightChevron width={20} height={20} />
        </button>
      </div>
      <TabGroup
        tabs={[
          {
            label: t("dashboard.announcements.tabGeneral"),
            content: null,
          },
          {
            label: t("dashboard.announcements.tabPrivate"),
            content: null,
          },
        ]}
        defaultActiveTab={activeTab}
        onTabChange={(index) => {
          setActiveTab(index);
          setPage(1);
        }}
        orientation="horizontal"
      />
      <ul className={styles.list}>
        {items.map((item) => (
          <li key={item.id} className={styles.item}>
            <div className={styles.itemImageWrap}>
              {item.imageUrl ? (
                <img src={item.imageUrl} alt="" className={styles.itemImage} />
              ) : (
                <div className={styles.itemImagePlaceholder} />
              )}
            </div>
            <div className={styles.itemBody}>
              <div className={styles.itemTopRow}>
                <h4 className={styles.itemTitle}>{item.title}</h4>
                {item.time && (
                  <span className={styles.itemTime}>{item.time}</span>
                )}
              </div>
              <div className={styles.itemBottomRow}>
                <span className={styles.tagWrap}>
                  <Chip
                    type={TAG_CHIP_TYPE[item.tagType]}
                    variant="soft"
                    size="small"
                  >
                    {item.tag}
                  </Chip>
                </span>
                <button
                  type="button"
                  className={styles.detailLink}
                  onClick={() =>
                    navigate(viewAllPath, {
                      state: { activeTab },
                    })
                  }
                >
                  {t("dashboard.announcements.detail")}
                  <ArrowRightLong
                    width={14}
                    height={14}
                    color="#E7212B"
                    className={styles.detailArrow}
                  />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.paginationArrow}
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ArrowLeftIcon height={16} width={16} />
          </button>
          {renderPaginationNumbers(page, totalPages, setPage)}
          <button
            type="button"
            className={styles.paginationArrow}
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <ArrowRightIcon height={16} width={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default DashboardAnnouncementsList;
